import { motion } from "framer-motion";
import { 
  CheckCircle, 
  ArrowRight,
  MessageCircle,
  AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function ThankYouPage() {
  return (
    <div className="min-h-screen bg-galvani-bg relative overflow-hidden flex flex-col">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-galvani-bg via-galvani-surface to-galvani-bg" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-galvani-primary/10 rounded-full blur-3xl" />

      {/* Header */}
      <header className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <a href="/" className="flex items-center w-fit">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 645.4 195.7" className="h-8 w-auto">
              <g fill="#FFFFFF">
                <path d="M367.6,80.7c-1-2.1,0.3-4.4,2.9-4.4h17.3c1.6,0,2.6,1,2.9,1.9l16,47.4h0.9L437.3,49c0.7-2,3.4-2.4,4.9-0.9L453.2,60c1.3,1.4,1.6,3.6,0.7,5.4l-43.4,94.1c-0.6,1-1.4,1.9-2.9,1.9H406c-1.6,0-2.4-0.9-2.9-1.9L367.6,80.7z"/>
                <path d="M320,59.6c0-1.3,1-2.4,2.4-2.4h12.7c1.3,0,2.4,1.1,2.4,2.4l1.1,76l33.4-2.3c1.4,0,2.4,1.1,2.4,2.4v11.9c0,1.3-1,2.4-2.4,2.4h-49.8c-1.4,0-2.4-1.1-2.4-2.4V59.6H320z"/>
                <path d="M624,59.5c0-1.3,1.1-2.4,2.4-2.4h12.1c1.3,0,2.4,1.1,2.4,2.4v88.1c0,1.3-1.1,2.4-2.4,2.4h-12.1c-1.3,0-2.4-1.1-2.4-2.4V59.5z"/>
                <path d="M538.8,57.9c-0.7-0.7-1.3-0.9-2.3-0.9h-2c-1.3,0-2.4,1-2.4,2.4v88.2c0,1.3,1.1,2.4,2.4,2.4h12c1.4,0,2.4-1.1,2.4-2.4V91.3h0.3l48,57.8c0.7,0.7,1.3,0.9,2.3,0.9h7.6c1.3,0,2.4-1,2.4-2.4v-14.3L538.8,57.9z"/>
                <path d="M609.6,126.1V59.5c0-1.3-1.1-2.4-2.4-2.4h-12c-1.4,0-2.4,1.1-2.4,2.4V102L609.6,126.1z"/>
                <path d="M224,146.6l40.8-88.2c0.4-0.9,1-1.4,2.3-1.4h1.3c1.3,0,1.9,0.7,2.3,1.4l40.4,88.2c0.9,1.7-0.3,3.4-2.3,3.4h-11.4c-2,0-2.9-0.9-3.9-2.6l-7.1-14.1h-37.7l-7.3,14.1c-0.6,1.3-1.7,2.6-3.9,2.6h-11.4C224.2,150,223.2,148.3,224,146.6z M279.6,119.9l-12.1-21.4l-12,21.4H279.6z"/>
                <path d="M225.7,98.4v2.3c0,26-13.3,49.1-41.8,49.1c-26.8,0-43.8-22.7-43.8-48.1c0-26.3,17.4-46.4,45-46.4c14.4,0,27.1,6.7,34.6,18.7c1.1,1.7,0.4,4-1.3,5l-9.6,5.1c-1.7,0.9-3.7,0.3-4.7-1.3c-4.1-6.6-11.1-10.6-19.4-10.6c-17.3,0-25.8,14.1-25.8,29.4s8.7,32.3,26,32.3c11.3,0,20.7-9.7,21-20.8l-16.7-1c-1.3-0.1-2.1-1.1-2.1-2.4V98.4H225.7z"/>
                <path d="M437,146.6l40.8-88.2c0.4-0.9,1-1.4,2.3-1.4h1.3c1.3,0,1.9,0.7,2.3,1.4l40.4,88.2c0.9,1.7-0.3,3.4-2.3,3.4h-11.4c-2,0-2.9-0.9-3.9-2.6l-7.1-14.1h-37.7l-7.3,14.1c-0.6,1.3-1.7,2.6-3.9,2.6h-11.4C437.3,150,436.3,148.3,437,146.6z M492.7,119.9l-12.1-21.4l-12,21.4H492.7z"/>
              </g>
            </svg>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex-1 flex items-center justify-center py-12 lg:py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Success Icon */}
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
            </div>

            {/* Headline */}
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mb-4">
              ¡Gracias!
              <br />
              <span className="text-galvani-primary">Tu guía está en camino</span>
            </h1>

            {/* Message */}
            <p className="font-body text-lg text-slate-400 mb-8 leading-relaxed max-w-lg mx-auto">
              Hemos enviado la guía completa a tu correo electrónico. 
              En unos minutos la tendrás en tu bandeja de entrada.
            </p>

            {/* Spam warning */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-10 max-w-md mx-auto"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="font-body text-sm text-amber-400 font-medium mb-1">
                    ¿No ves el email?
                  </p>
                  <p className="font-body text-xs text-slate-400">
                    Revisa tu carpeta de <span className="text-white">Spam</span> o <span className="text-white">Promociones</span>. 
                    A veces los correos automáticos terminan ahí.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Divider */}
            <div className="relative mb-10">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 bg-galvani-bg text-slate-500 text-sm font-body">
                  ¿Quieres dar el siguiente paso?
                </span>
              </div>
            </div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="p-8 rounded-2xl bg-galvani-surface border border-slate-800"
            >
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-white mb-4">
                ¿Prefieres que lo hagamos nosotros?
              </h2>
              <p className="font-body text-slate-400 text-sm mb-6 max-w-md mx-auto">
                Si después de leer la guía decides que prefieres un equipo profesional 
                que se encargue de todo, <span className="text-white">hablemos por WhatsApp</span>.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://wa.me/683397873?text=Hola, he descargado la guía de podcast y me gustaría información sobre vuestros servicios de producción."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="thankyou-whatsapp-btn"
                >
                  <Button className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-body font-semibold px-8 py-4 h-14 text-base rounded-full transition-all duration-300">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Contactar por WhatsApp
                  </Button>
                </a>
                <a
                  href="/"
                  data-testid="thankyou-info-btn"
                >
                  <Button 
                    variant="outline"
                    className="w-full sm:w-auto border-slate-600 hover:border-galvani-primary text-slate-300 hover:text-white font-body font-semibold px-8 py-4 h-14 text-base rounded-full transition-all duration-300 bg-transparent"
                  >
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Conocer más sobre Galvani
                  </Button>
                </a>
              </div>

              <p className="mt-4 text-xs text-slate-500 font-body">
                Sin compromiso. Te respondemos en menos de 24 horas.
              </p>
            </motion.div>

            {/* Back to home */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-10"
            >
              <a
                href="/"
                className="inline-flex items-center text-slate-400 hover:text-galvani-primary font-body text-sm transition-colors"
              >
                <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                Volver a la página principal
              </a>
            </motion.div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 font-body text-sm">
            © {new Date().getFullYear()} Galvani Studios. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
